/* eslint-disable no-nested-ternary */
import React, { CSSProperties, ReactNode, useEffect, useState, useRef } from "react";

import {
  BaseTextFieldProps,
  FormHelperText,
  InputAdornment,
  InputBaseComponentProps,
  OutlinedInputProps,
  TextFieldProps,
  Tooltip,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  PickerChangeHandlerContext,
  DateValidationError,
} from "@mui/x-date-pickers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import classNames from "classnames";
import { isValid, parseISO, format, isDate, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { Calendar } from "react-date-range";
import { FiCalendar } from "react-icons/fi";

import { Error } from "../../../models/error";
import { StyledMenu } from "./DateRange/styles";
import useStyles, {
  TextFieldCustomized,
  FormControlCustomized,
  WrapperLabel,
  WrapperInfo,
} from "./styles";

export interface InputProps extends BaseTextFieldProps {
  name?: string;
  type?: React.InputHTMLAttributes<unknown>["type"];
  accept?: string;
  label?: React.ReactNode;
  error?: boolean;
  infoMessage?: string;
  errorMessage?: string;
  errorMessageClass?: string;
  errorResponse?: Error;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  maxLength?: number;
  onChange?: OutlinedInputProps["onChange"];
  onBlur?: OutlinedInputProps["onBlur"];
  onFocus?: OutlinedInputProps["onFocus"];
  formControlStyle?: CSSProperties;
  min?: string | number | Date;
  max?: string | number | Date;
  step?: number;
  inputProps?: InputBaseComponentProps;
  bordercolor?: string;
  formControlClassName?: string;
  dateButtonSlot?: (params: { onClick: () => void }) => React.ReactNode;
  labelClassName?: string;
  showDateInput?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  inputTextAlign?: "start" | "center" | "end";
  errorBreakLine?: boolean;
  "data-cy"?: string;
  onInputInvalidate?: (value: boolean) => void;
  renderIconInsideLabel?: () => React.ReactNode;
}

const TextField = (fieldProps: TextFieldProps) => (
  <TextFieldCustomized {...fieldProps} placeholder="dd/mm/yyyy" helperText="" variant="outlined" />
);

const InputTextField = (props: InputProps) => {
  const {
    label,
    error,
    errorMessage,
    errorMessageClass,
    infoMessage = "",
    disabled,
    fullWidth,
    maxLength,
    min,
    max,
    step,
    leftIcon,
    rightIcon,
    formControlClassName,
    formControlStyle,
    labelClassName,
    defaultValue,
    value,
    type,
    onChange,
    disableFuture,
    disablePast,
    inputTextAlign,
    required,
    errorBreakLine = true,
    errorResponse,
    renderIconInsideLabel,
    showDateInput = true,
    dateButtonSlot,
    onInputInvalidate,
    ...rest
  } = props;

  const divRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);
  const open: boolean = !!anchorEl;

  const [dateIsInValid, setDateIsInValid] = useState<boolean>(false);
  const [date, setDate] = useState<Date>();

  const classes = useStyles({ disabled });
  const hasError = error || dateIsInValid || (errorResponse && errorResponse?.field === rest.name);

  useEffect(() => {
    if (type !== "date") return;

    if (typeof value === "string") {
      const valueSplitted = value.split("-");

      if (valueSplitted.length === 3) {
        const year = valueSplitted[0];
        const month = valueSplitted[1];
        const day = valueSplitted[2];
        const dateObject = parse(`${year}-${month}-${day}`, "yyyy-MM-dd", new Date());

        setDate(dateObject);

        return;
      }
    }

    setDate(undefined);
  }, [value]);

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderInfoLabel = () => {
    if (!required && infoMessage?.trim().length) {
      return (
        <Tooltip title={infoMessage} placement="right">
          <WrapperInfo>
            <InfoOutlinedIcon className={classes.extraSmallIcon} />
          </WrapperInfo>
        </Tooltip>
      );
    }

    return null;
  };

  const renderLabel = () => {
    if (label) {
      if (!required && infoMessage?.trim().length) {
        return (
          <WrapperLabel>
            <span className={classNames(classes.label, labelClassName)}>{label}</span>
            {renderInfoLabel()}
          </WrapperLabel>
        );
      }

      return (
        <span className={classNames(classes.label, labelClassName)}>
          <WrapperLabel>
            {label} {renderRequiredLabel()}
          </WrapperLabel>
          {renderIconInsideLabel?.()}
        </span>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText
          className={classNames(classes.formHelper, errorMessageClass)}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          {errorMessage}
        </FormHelperText>
      );
    }
    if (errorResponse && errorResponse?.field === rest.name) {
      return (
        <FormHelperText
          className={classes.formHelper}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          {errorResponse?.message}
        </FormHelperText>
      );
    }
  };

  const handleInvalidation = (value: boolean) => {
    if (onInputInvalidate) {
      onInputInvalidate(value);

      return;
    }

    setDateIsInValid(value);
  };

  const getDateFormated = (value: Date) => {
    return format(value, "yyyy-MM-dd");
  };

  const executeOnChange = (value?: string | null) => {
    onChange?.({
      target: {
        value,
      },
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>);
  };

  const handleOnDateChange = (
    value: Date | null,
    _context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    setDate(
      value ? zonedTimeToUtc(value, Intl.DateTimeFormat().resolvedOptions().timeZone) : undefined
    );

    try {
      handleInvalidation(true);

      if (!value) {
        executeOnChange(null);

        handleInvalidation(false);

        return;
      }

      const dateFormatted = getDateFormated(value);

      if (isValid(parseISO(dateFormatted))) {
        handleInvalidation(false);

        executeOnChange(dateFormatted);

        return;
      }
      handleInvalidation(true);
    } catch (err) {
      handleInvalidation(true);
    }
  };

  const renderDateInput = () => {
    const minDate = min ? new Date(min) : undefined;

    const extraAttrs = {
      "data-cy": rest["data-cy"] as string,
    };

    return (
      <div ref={divRef}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            name={rest?.name}
            disabled={disabled}
            format="dd/MM/yyyy"
            minDate={minDate}
            value={date}
            disableFuture={disableFuture}
            disablePast={disablePast}
            open={false}
            onOpen={() => {
              setAnchorEl(divRef.current);
            }}
            onChange={handleOnDateChange}
            slotProps={{
              textField(states) {
                return {
                  fullWidth,
                  placeholder: "dd/mm/yyyy",
                  error: error || dateIsInValid,
                  disabled,
                  ...states,
                  ...extraAttrs,
                };
              },
            }}
            slots={{
              openPickerIcon: () => <FiCalendar className={classes.keyboardDatepickerIcon} />,
              textField: TextField,
            }}
          />
        </LocalizationProvider>

        <StyledMenu
          id="date-range-picker"
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Calendar
            showDateDisplay={false}
            preventSnapRefocus
            minDate={min ? (isDate(min) ? (min as Date) : new Date(min)) : undefined}
            maxDate={max ? (isDate(max) ? (max as Date) : new Date(max)) : undefined}
            date={date}
            dateDisplayFormat="dd/MM/yyyy"
            onChange={(date) => {
              handleInvalidation(false);
              setDate(date);

              executeOnChange(getDateFormated(date));

              setAnchorEl(null);
            }}
          />
        </StyledMenu>
      </div>
    );
  };

  const renderTextInput = () => {
    return (
      <TextFieldCustomized
        fullWidth={fullWidth}
        defaultValue={!value && value !== "" && defaultValue ? defaultValue : undefined}
        value={value || ""}
        variant="outlined"
        inputProps={{
          maxLength,
          min,
          max,
          step,
          style: {
            textAlign: rest.style?.textAlign ? rest.style?.textAlign : inputTextAlign,
          },
        }}
        onKeyDown={(event: any) => type === "number" && event.key === "e" && event.preventDefault()}
        onChange={onChange}
        InputProps={{
          startAdornment: leftIcon ? (
            <InputAdornment position="start">{leftIcon}</InputAdornment>
          ) : undefined,
          endAdornment: rightIcon ? (
            <InputAdornment position="end">{rightIcon}</InputAdornment>
          ) : undefined,
        }}
        disabled={disabled}
        error={hasError}
        type={type}
        {...rest}
      />
    );
  };

  const renderInputType = () => {
    switch (type) {
      case "date":
        return renderDateInput();
      default:
        return renderTextInput();
    }
  };

  return (
    <FormControlCustomized
      fullWidth={fullWidth}
      style={formControlStyle}
      className={formControlClassName}
      disabled={disabled}
      error={hasError}
    >
      {renderLabel()}
      {renderInputType()}
      {renderError()}
    </FormControlCustomized>
  );
};

export default InputTextField;
